import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildNewsArticlesColumns } from '../columns';
import {
  NEWS_ARTICLE_SORT_KEYS,
  type NewsArticle,
  type NewsArticleSortKey,
  type SortDirection,
} from '../schema';
import type { CommonResponse } from '@/types/api';

// AllCommunityModule pulls in column-state APIs (api.getColumnState used by
// readSort) plus everything else community. v33 splits APIs into modules and
// would otherwise raise error #200 when getColumnState is called.
ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<NewsArticle> | undefined;
  onEdit: (row: NewsArticle) => void;
  onDelete: (row: NewsArticle) => void;
  onSortChange: (sort: { sortBy?: NewsArticleSortKey; sortDir?: SortDirection }) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  // Whitelisted columns opt in via `sortable: true`. Anything else is
  // explicitly non-sortable to avoid the "looks-sorted-but-isn't" trap.
  sortable: false,
  filter: false,
  minWidth: 100,
  // Show a faded ⇅ indicator on sortable columns even before they're sorted,
  // so editors can see at a glance which headers are clickable. ag-grid only
  // renders this when `sortable: true`, so non-sortable cols stay clean.
  unSortIcon: true,
  // NOTE: previously had `comparator: () => 0` here to suppress client-side
  // re-sort, but it appears to also suppress the onSortChanged event in
  // ag-grid v33. Client-side sort is correct on visible rows (numeric/date
  // fields compare properly); server refetch replaces with the global order.
};

const SORT_KEY_SET = new Set<string>(NEWS_ARTICLE_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: NewsArticleSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as NewsArticleSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function NewsArticlesGrid({ data, onEdit, onDelete, onSortChange }: Props) {
  const columnDefs = useMemo(
    () => buildNewsArticlesColumns({ onEdit, onDelete }),
    [onEdit, onDelete],
  );

  return (
    <div className="h-full w-full">
      <AgGridReact<NewsArticle>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
